import type { ITelemetryItem } from "@microsoft/applicationinsights-web";

export const REDACTED = "[REDACTED]";

/** Keys that hold credentials: access, refresh and ID tokens, auth headers, cookies, passwords, secrets and API keys. */
const sensitiveKeyPattern = /token|authorization|cookie|password|secret|api[_-]?key/i;
/** A Bearer credential, as sent in an Authorization header. */
const bearerPattern = /\bbearer\s+[\w+./~-]+=*/gi;
/** A JWT: base64url header (`{"` encodes to `eyJ`), payload and signature. */
const jwtPattern = /\beyJ(?:[\w-]+\.){2}[\w-]*/g;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

export const isSensitiveKey = (key: string) => sensitiveKeyPattern.test(key);

export const redactSensitiveText = (text: string) => text.replaceAll(bearerPattern, REDACTED).replaceAll(jwtPattern, REDACTED);

/**
 * JSON.stringify for logging request inputs: values of credential keys and of every request header are replaced,
 * and JWT or Bearer strings are masked wherever they appear.
 */
export const stringifyForTelemetry = (value: unknown): string =>
    JSON.stringify(value, (key, nested: unknown) => {
        if (isSensitiveKey(key)) {
            return REDACTED;
        }
        // oRPC's detailed inputs carry request headers, such as the ECS user token in recoveryApi.
        if (key === "headers" && isRecord(nested)) {
            return Object.fromEntries(Object.keys(nested).map((name) => [name, REDACTED]));
        }
        return typeof nested === "string" ? redactSensitiveText(nested) : nested;
    });

const redactProperties = (properties: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [key, isSensitiveKey(key) ? REDACTED : typeof value === "string" ? redactSensitiveText(value) : value])
    );

/**
 * App Insights telemetry initializer: masks credentials in custom properties and trace messages, whichever code logged them.
 * Initializers work on the item they're given (the SDK sends that item; a returned one would be ignored), so this sets
 * redacted copies on it and leaves the objects the caller passed to trackException or trackTrace unchanged.
 */
export const redactSensitiveTelemetry = (envelope: ITelemetryItem) => {
    // trackException puts its properties in baseData; trackTrace and trackEvent put theirs in data.
    const { baseData, data } = envelope;
    if (baseData) {
        envelope.baseData = {
            ...baseData,
            ...(isRecord(baseData.properties) ? { properties: redactProperties(baseData.properties) } : {}),
            ...(typeof baseData.message === "string" ? { message: redactSensitiveText(baseData.message) } : {})
        };
    }
    if (isRecord(data)) {
        envelope.data = redactProperties(data);
    }
};
